import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Store,
  Eye,
  EyeOff,
  Phone,
  Lock,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedPhone = phoneNumber.trim();
    const trimmedPw = password.trim();

    if (!trimmedPhone || !trimmedPw) {
      setError("Please enter your phone number and password.");
      return;
    }
    // Basic length check — accept any format the backend uses
    if (trimmedPhone.length < 7) {
      setError("Enter a valid phone number.");
      return;
    }

    setLoading(true);
    const result = await login(trimmedPhone, trimmedPw);
    setLoading(false);

    if (result.success) {
      navigate(result.defaultPath || (result.role === "tenant" ? "/tenant" : "/dashboard"), {
        replace: true,
      });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#eef2ff_42%,#ffffff_100%)]">
      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] px-6 py-8 sm:px-8 lg:px-10">
        <div className="hidden lg:flex flex-col justify-between rounded-[2rem] border border-white/70 bg-white/80 p-10 shadow-[0_24px_80px_rgba(79,70,229,0.08)] backdrop-blur">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 shadow-[0_10px_30px_rgba(79,70,229,0.28)]">
                <Store size={21} className="text-white" />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900">ShopOwnaa</p>
                <p className="text-xs text-gray-500">Marketplace Platform</p>
              </div>
            </div>

            <div className="mt-14 max-w-xl space-y-6">
              <span className="inline-flex rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-violet-600">
                Profiler Portal
              </span>
              <div>
                <h2 className="text-5xl font-black leading-[1.05] text-gray-900">
                  Run tenant and seller work
                  <span className="block gradient-text">from one light-first workspace.</span>
                </h2>
                <p className="mt-5 max-w-lg text-sm leading-7 text-gray-600">
                  Access property management, seller operations, plan payments,
                  and profile tools from the same ShopOwnaa account without
                  switching systems.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  "Tenant leasing and receipts",
                  "Marketplace plans and renewals",
                  "Incoming orders and products",
                  "Single profiler account access",
                ].map((feature) => (
                  <div
                    key={feature}
                    className="rounded-2xl border border-violet-100 bg-violet-50/70 px-4 py-3 text-sm font-medium text-gray-700">
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-gradient-to-r from-white to-slate-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-500">
              Secure Access
            </p>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Your session is authenticated directly against the ShopOwnaa API.
              Contact your market administrator if you need a new account or a password reset.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center py-6 lg:py-0 lg:pl-10">
          <div className="w-full max-w-md rounded-[2rem] border border-gray-200/80 bg-white p-7 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-9">
            <div className="mb-10 flex items-center gap-3 lg:hidden">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 shadow-[0_10px_30px_rgba(79,70,229,0.24)]">
                <Store size={18} className="text-white" />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900">ShopOwnaa</p>
                <p className="text-xs text-gray-500">Marketplace Platform</p>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-violet-600">
                Welcome Back
              </p>
              <h1 className="mt-3 text-3xl font-black text-gray-900">
                Sign in to your profiler workspace
              </h1>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                Use the phone number and password registered on your ShopOwnaa account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {error && (
                <div className="flex items-center gap-2.5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                  Phone number
                </label>
                <div className="relative">
                  <Phone
                    size={15}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="tel"
                    className="input-field pl-10"
                    placeholder="08012345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    autoComplete="tel"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Enter the phone number linked to your tenant or seller profile.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                  Password
                </label>
                <div className="relative">
                  <Lock
                    size={15}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type={showPw ? "text" : "password"}
                    className="input-field pl-10 pr-10"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                    tabIndex={-1}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Use the password provided when your account was created.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary mt-2 w-full justify-center py-3 text-base shadow-[0_16px_32px_rgba(79,70,229,0.18)]">
                {loading ? (
                  <>
                    <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>Sign in to Dashboard</>
                )}
              </button>
            </form>

            <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
              <div className="flex items-start gap-2.5">
                <ShieldCheck size={16} className="mt-0.5 flex-shrink-0 text-violet-500" />
                <p className="text-xs leading-6 text-gray-600">
                  Credentials are sent securely. If you do not have an account,
                  contact your market administrator to get set up.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
